
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import ActionButton from "@/components/ActionButton.vue";
import ReorderSettingTable from "@/components/ReorderSettingTable.vue";
import { StoreModel } from "@/models/store";
import { EventBus } from "@/main";
import { SupplierList } from "@/collections/suppliers";
import { SupplierModel } from "@/models/supplier";
import { ReorderSettingParam } from "@/models/reorder_setting_param";
import { StoreLocationList } from "@/collections/store_locations";
import { StoreLocationModel } from "@/models/store_location";
import ReorderParamForm from './ReorderParamForm.vue';
import { ReportModel } from "@/models/report";
import { MeasureModel } from "@/models/measure";
import { DimensionModel } from "@/models/dimension";
import { FilterColumnList } from "@/collections/filter_columns";
import { FilterColumnModel } from "@/models/filter_column";
import ReportFilter from "@/components/ReportFilter.vue";
import { AnalyticsTypeModel } from "@/models/analytics_type";
import { ParamModel } from "@/models/param";
import { ProductSegmentModel } from "@/models/product_segment";
import { MeasureList } from "@/collections/measures";
import { ReplenishmentSettingHelper } from '@/helpers/ReplenishmentSettingHelper';
import SpreadsheetSettings from '@/components/SpreadsheetSettings.vue';
import { ReplenishmentSettingHistory } from '@/models/replenishment_setting_history';


interface Option {
  sortBy?: any;
  sortDesc?: any;
  page?: number;
  itemsPerPage?: number;
  multiSort?: boolean;
}

interface FilterData {
  filterColumns: FilterColumnList;
  productSegment: ProductSegmentModel;
}

interface TableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
  filters?: FilterColumnList;
}

interface ScopeFilter {
  id: number | null;
  operator: 'eq' | 'ne' | 'all' | 'null';
  scope: 'supplier' | 'location';
}

@Component({
  components: {
    ActionButton,
    ReorderSettingTable,
    ReorderParamForm,
    ReportFilter,
    SpreadsheetSettings
  },
})
export default class InventoryParamSetting extends Vue {
  @Prop({ required: true })
  store!: StoreModel;
  public loadingParams: boolean = false;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  public measure: MeasureModel = new MeasureModel();
  public dimension: DimensionModel = new DimensionModel();
  public analyticsType: AnalyticsTypeModel = new AnalyticsTypeModel();
  public readyToGetReport: boolean = false;
  public needRefreshData: boolean = false;
  public dates: string[] = [];
  public defaultRange: string = "30d";
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public hasSchemas!: boolean;

  private search = '';
  private selectedProducts: any[] = [];
  private saving = false;

  // New properties
  private productScope: 'product' | 'variant' = 'variant';
  private locations: StoreLocationModel[] = [];
  private selectedLocationId: number | null = null;
  private loadingLocations = false;

  // New properties for param dialog
  private paramDialogVisible = false;
  private currentParams = new ReorderSettingParam();
  private currentSupplier: SupplierModel | null = null;
  private currentlyEditingVariantId: string | null = null;
  private paramToConfirm = new ReorderSettingParam();
  private useSpreadsheets = false;
  // New properties for supplier scope
  private locationScope: 'global' | 'specific' = 'global';
  private parameterScope: 'global' | 'specific' = 'global';
  private reloadingProducts = false;
  private filterData: FilterData = {
    filterColumns: new FilterColumnList(),
    productSegment: new ProductSegmentModel()
  };
  private filterReportColumns: any[] = [
  {
    "name": "Product Tags",
    "code": "product_tags",
    "dataType": "text",
    "luisMapping": "product_tags"
  },
  {
    "name": "Collections", 
    "code": "product_collections",
    "dataType": "text",
    "luisMapping": "product_collections"
  },
  {
    "name": "Product",
    "code": "Item[Item Description]",
    "dataType": "text", 
    "luisMapping": "by_product"
  },
  {
    "name": "SKU",
    "code": "product_sku",
    "dataType": "text",
    "luisMapping": "by_product_sku"
  },
  {
    "name": "Product Type",
    "code": "product_type", 
    "dataType": "text",
    "luisMapping": "by_product_type"
  },
  {
    "name": "Vendor",
    "code": "Vendor Name",
    "dataType": "text",
    "luisMapping": "by_vendor"
  },
  {
    "name": "Variant",
    "code": "variant",
    "dataType": "text",
    "luisMapping": "variant",
    "autoSelect": false,
    "colorIndex": false,
    "sortable": true
  },
  {
    "name": "Barcode",
    "code": "product_barcode", 
    "dataType": "text",
    "luisMapping": "product_barcode",
    "autoSelect": false,
    "colorIndex": false,
    "sortable": true
  }    
  ];

  private confirmDialogVisible = false;
  private totalItems = 0;
  private isSelectAll = false;
  private showGuideDialog = false;
  private guideStep = 1;
  private showProcessingWarning = false;

  async created() {
    await this.fetchGlobalParams();
    await this.fetchLocations();
  }

  private handleTotalItemsUpdate(totalItems: number) {
    this.totalItems = totalItems;
  }

  public async initData() {
    const mesaureCode = "replenishment_settings";
    this.measure.code = mesaureCode;
    if (this.measures.items.length === 0) {
        await this.measure.fetch();
    } else {
      for (const item of this.measures.items) {
        if (item.code === mesaureCode) {
          this.measure = item;
          break;
        }
      }
    }
    
    for (const item of this.measure.dimensions.items) {
      if (item.code === "by_variant") {
        this.dimension = item;
        break;
      }
    }

  }

  private async fetchGlobalParams() {
    if (this.parameterScope === 'global') {
      this.loadingParams = true;
      try {
        this.currentParams = await ReplenishmentSettingHelper.getGlobalParams(
          {
            product: this.productScope,
            location: this.locationScope,
            parameter: this.parameterScope,
            supplier: ''
          },
          this.selectedLocationId || 0,
        );
      } catch (error) {
        console.error('Error fetching global params:', error);
        this.handleError('Failed to load global parameters');
      } finally {
        this.loadingParams = false;
      }
    }
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const locations = new StoreLocationList();
      await locations.fetch();
      this.locations = locations.items;
    } catch (error) {
      console.error('Error fetching stores:', error);
      this.handleError('Failed to load stores');
    } finally {
      this.loadingLocations = false;
    }
  }

  @Watch('productScope')
  onProductScopeChange(newValue: string) {
    // Reset selected products when switching scope
    this.selectedProducts = [];
    // Reset filter data
    this.filterData = {
      filterColumns: new FilterColumnList(),
      productSegment: new ProductSegmentModel()
    };
  }

  @Watch('locationScope')
  onLocationScopeChange(newValue: string) {
    if (newValue === 'global') {
      this.selectedLocationId = null;
      this.fetchGlobalParams();
    }
    // Reset selected products when switching supplier scope
    this.selectedProducts = [];
  }

  @Watch('parameterScope')
  onParameterScopeChange(newValue: string) {
    // Reset selected products when switching parameter scope
    this.selectedProducts = [];
      // Reset filter data
      this.filterData = {
        filterColumns: new FilterColumnList(),
        productSegment: new ProductSegmentModel()
      };
    if (newValue === 'global') {
      this.fetchGlobalParams();
    } else {
      this.currentParams = new ReorderSettingParam();
    }
  }

  @Watch('selectedLocationId')
  onSelectedLocationIdChange(newValue: number) {
    if (newValue && this.parameterScope === 'global') {
      this.currentParams.locationId = newValue;
      this.fetchGlobalParams();
    }
  }

  private resetLocationFilter() {
    this.selectedLocationId = null;
  }

  private handleError(message: string) {
    EventBus.$emit('show-snackbar', message, 'error');
  }

  private get getSelectedLocationName(): string {
    if (this.selectedLocationId) {
      const location = this.locations.find(l => l.id === this.selectedLocationId);
      return location ? location.name : '';
    }
    return '';
  }

  private async applySetting(paramToUpdate: ReorderSettingParam) {
    // Check form validity first
    const form = this.$refs.paramForm as any;
    if (!form) {
      EventBus.$emit('show-snackbar', 'Form reference not found', 'error');
      return;
    }

    // Get the form element from the ReorderParamForm component
    const vForm = form.$refs.form as any;
    if (!vForm) {
      EventBus.$emit('show-snackbar', 'Form element not found', 'error');
      return;
    }

    const isValid = await vForm.validate();
    if (!isValid) {
      EventBus.$emit('show-snackbar', 'Please fix the validation errors before proceeding', 'error');
      return;
    }

    // Check if at least one parameter is set
    const hasValue = 
      paramToUpdate.minInventoryQuantity !== undefined ||
      paramToUpdate.maxInventoryQuantity !== undefined ||
      paramToUpdate.minStockCoverDays !== undefined ||
      paramToUpdate.maxStockCoverDays !== undefined;

    if (!hasValue) {
      EventBus.$emit('show-snackbar', 'Please set at least one parameter before proceeding', 'warning');
      return;
    }

    // Show confirmation dialog only if form is valid and has at least one parameter
    this.paramToConfirm = new ReorderSettingParam();
    Object.assign(this.paramToConfirm, paramToUpdate);
    this.confirmDialogVisible = true;
  }

  private async confirmUpdate() {
    if (!this.paramToConfirm) return;
    
    const filterColumns = new FilterColumnList();
    this.paramToConfirm.scope = this.productScope;

    // Handle Supplier scope
    if (this.locationScope === 'specific') {
      if (!this.selectedLocationId) {
        EventBus.$emit('show-snackbar', 'Please select a location', 'warning');
        return;
      }
      
      this.paramToConfirm.locationId = this.selectedLocationId;
      let locationFilterColumn = new FilterColumnModel();
      locationFilterColumn.code = 'location_id';
      locationFilterColumn.value = [this.selectedLocationId.toString()];
      locationFilterColumn.operator = 'eq';
      filterColumns.add(locationFilterColumn);
    }

    // Handle Parameter Scope and Selection
    if (this.parameterScope === 'specific') {
      if (this.isSelectAll) {
        // If select all is true, use the filter data's filter columns
        this.filterData.filterColumns.items.forEach(filter => {
          filterColumns.add(filter);
        });
      } else {
        // If specific products are selected, create filter based on selection
        const idField = this.productScope === 'product' ? 'product_id' : 'variant_id';
        let selectionFilter = new FilterColumnModel();
        selectionFilter.code = idField;
        selectionFilter.operator = 'eq';
        selectionFilter.value = this.selectedProducts.map(product => product[idField]);
        
        if (selectionFilter.value.length > 0) {
          filterColumns.add(selectionFilter);
        } else {
          EventBus.$emit('show-snackbar', 'Please select at least one item', 'warning');
          return;
        }
      }
    }

    this.saving = true;
    try {
      const setting = {
        date: new Date().toISOString(),
        inventoryParam: this.paramToConfirm,
        scope: {
          product: this.productScope,
          location: this.locationScope,
          parameter: this.parameterScope,
          supplier: ''
        }
      };
      
      await this.paramToConfirm.saveReorder(filterColumns, setting, {
        product: this.productScope,
        location: this.locationScope,
        parameter: this.parameterScope,
        supplier: ''
      });
      this.selectedProducts = [];
      EventBus.$emit('show-snackbar', 'Parameters updated successfully', 'success');
      this.confirmDialogVisible = false;
      
      // Refresh the table to show updated values
      await this.reloadProducts();
    } catch (error) {
      console.error('Error saving products:', error);
      EventBus.$emit('show-snackbar', 'Failed to save products', 'error');
    } finally {
      this.saving = false;
    }
  }

  private customFilter(item: string, queryText: string, itemText: string): boolean {
    const text = itemText.toLowerCase();
    const searchText = queryText.toLowerCase();
    return text.indexOf(searchText) > -1;
  }

  private createParamsFromItem(item: any): ReorderSettingParam {
    const params = new ReorderSettingParam();
    
    // Set parameters from item data
    params.minInventoryQuantity = item['[min_inventory_quantity]'];
    params.maxInventoryQuantity = item['[max_inventory_quantity]'];
    params.minStockCoverDays = item['[min_stock_cover_days]'];
    params.maxStockCoverDays = item['[max_stock_cover_days]'];

    if (this.selectedLocationId) {
      params.locationId = this.selectedLocationId;
    }

    return params;
  }

  private async reloadProducts() {
    this.reloadingProducts = true;
    this.showProcessingWarning = false;
    try {
      // Check status first
      const history = new ReplenishmentSettingHistory();
      const lastSettingStatus = await history.getLastSettingStatus();

      if (lastSettingStatus === 'COMPLETED') {
        if (this.$refs.productTable) {
          const table = this.$refs.productTable as any;
          await table.fetchProducts();
        }
      } else if (lastSettingStatus === 'PROCESSING') {
        // Show warning if not completed
        this.showProcessingWarning = true;
        console.log('Replenishment settings still processing. Table refresh skipped.');
      }
    } catch (error) {
      console.error('Error during product reload or status check:', error);
      this.handleError('Failed to check status or reload products');
    } finally {
      this.reloadingProducts = false;
    }
  }

  private get isGlobalSetting(): boolean {
    return this.locationScope === 'global' && this.selectedProducts.length === 0;
  }

  private selectAll() {
    this.isSelectAll = true;
    if (this.$refs.productTable) {
      const table = this.$refs.productTable as any;
      if (table.products && table.products.length > 0) {
        this.selectedProducts = [...table.products];
      }
    }
  }

  private clearSelection() {
    this.isSelectAll = false;
    this.selectedProducts = [];
  }
}
